import { UnitDto } from 'api/units/models/UnitDto';
import BudgetTotals from './budgetTotals/BudgetTotals';
import { TaxDto } from 'api/taxes/models/TaxDto';
import { LinesDataProvider } from '../lines/LinesDataProvider';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Panel from 'common/components/panel/Panel';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { BudgetDto } from 'api/budgets/models/BudgetDto';
import { RichInputController } from 'common/components/richTextInput/RichTextInput';
import { SmallTabs, Tab } from 'common/components/smallTabs/SmallTabs';
import { useEffect, useState } from 'react';
import LanguagesService from 'api/languages/LanguagesService';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE } from 'Config';

interface Props {
    isDetails: boolean;
    units: UnitDto[];
    taxes: TaxDto[];
}

function BudgetPrices({ isDetails, units, taxes }: Props): JSX.Element | null {
    const { t } = useTranslation();
    const form = useFormContext<BudgetDto>();
    const [languages, setLanguages] = useState<SelectValueLabel[]>([]);
    const [activeTab, setActiveTab] = useState<string>('');

    const { fields, append } = useFieldArray({
        control: form.control,
        name: 'paymentTermsTranslations',
        keyName: '_key',
    });

    useEffect(() => {
        (async () => {
            try {
                const langs = await LanguagesService.GetAllForSelectItem();
                setLanguages(langs);
                const isos = langs.map(l => l.label ?? '').filter(Boolean);
                // Ensure the form has an entry per language
                const current = form.getValues('paymentTermsTranslations') ?? [];
                for (const iso of isos) {
                    if (!current.some(x => x.languageIso === iso)) {
                        append({ languageIso: iso, content: '' });
                    }
                }
                if (!activeTab && isos.length > 0) {
                    setActiveTab(isos[0]);
                }
            } catch (error) {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Failed to load languages', error);
            }
        })();
    }, []);

    const fieldIndex = (iso: string) => fields.findIndex(f => f.languageIso === iso);

    const languageName = (iso: string): string => {
        const fallback = languages.find(l => l.label === iso)?.fullLabel ?? iso;
        const key = `languages.${iso}`;
        const translated = t(key);
        return translated === key ? fallback : translated;
    }

    return (
        <div>
            <LinesDataProvider.Provider value={{ taxes, units, suppliers: [], hiddenColumns: [] }}>
                <Row>
                    <Col xs={12} md={6}>
                        <Panel title={t('budgets.budget.totals')}>
                            <BudgetTotals
                                disabled={isDetails}
                            />
                        </Panel>
                    </Col>
                    <Col xs={12} md={6}>
                        <Panel title={t('budgets.budget.payment_terms')} style={{ overflow: 'unset' }}>
                            {languages.length > 0 && activeTab && (
                                <SmallTabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                                    {languages.map(lang => {
                                        const iso = lang.label ?? '';
                                        const idx = fieldIndex(iso);
                                        if (idx < 0) {
                                            return (
                                                <Tab key={iso} eventKey={iso} title={languageName(iso)}>
                                                    <span />
                                                </Tab>
                                            );
                                        }
                                        return (
                                            <Tab key={iso} eventKey={iso} title={languageName(iso)}>
                                                <RichInputController
                                                    name={`paymentTermsTranslations.${idx}.content` as const}
                                                    control={form.control}
                                                    disabled={isDetails}
                                                />
                                            </Tab>
                                        );
                                    })}
                                </SmallTabs>
                            )}
                        </Panel>
                    </Col>
                </Row>
            </LinesDataProvider.Provider>
        </div>
    );
}

export default BudgetPrices;
